import { Button } from "@gigpro/ui/Button";
import type { AiInterviewInviteResponse } from "@sidegigllc/api/client";
import { formatDistanceToNow } from "date-fns";
import { type AiInterviewState, AiInterviewStateChip } from "./AiInterviewStateChip";

interface AiInterviewCardProps extends Pick<AiInterviewInviteResponse, "sent_at" | "expires_at" | "completed_at"> {
  state: AiInterviewState;
  isLoading?: boolean;
  /** When false (pro has no phone), the send action is disabled — nothing to text. */
  canSend?: boolean;
  onSend: () => void;
}

const rel = (date: Date | null) => (date ? formatDistanceToNow(date, { addSuffix: true }) : null);

const relPhrase = (date: Date | null, phrase: (rel: string) => string, fallback = "") => {
  const r = rel(date);
  return r ? phrase(r) : fallback;
};

function getBodyText({
  state,
  sent_at,
  expires_at,
  completed_at,
}: Pick<AiInterviewCardProps, "state" | "sent_at" | "expires_at" | "completed_at">): string {
  switch (state) {
    case "PENDING": {
      const priorCompletion = relPhrase(completed_at, (r) => ` A prior interview was completed ${r}.`);
      const sentPhrase = relPhrase(sent_at, (r) => `Interview link sent ${r}.`, "An invite is pending.");
      const expiryPhrase = relPhrase(expires_at, (r) => ` Expires ${r}.`);
      return `${sentPhrase}${expiryPhrase} Not completed yet.${priorCompletion}`;
    }
    case "EXPIRED": {
      const sentPhrase = relPhrase(sent_at, (r) => ` (sent ${r})`);
      return `The last invite${sentPhrase} expired without completion.`;
    }
    case "COMPLETED": {
      const completedPhrase = relPhrase(completed_at, (r) => ` ${r}`);
      return `Interview completed${completedPhrase}.`;
    }
    case "PAUSED": {
      const sentPhrase = relPhrase(sent_at, (r) => ` (link sent ${r})`);
      const expiryPhrase = relPhrase(expires_at, (r) => ` The link expires ${r}.`);
      return `The pro started the interview${sentPhrase} then paused it.${expiryPhrase} A partial transcript exists, so it may still be completable.`;
    }
    case "EXHAUSTED": {
      const sentPhrase = relPhrase(sent_at, (r) => ` (sent ${r})`);
      return `The pro has used up every attempt on the last invite${sentPhrase} and can't start another on their own. Send a new link to give them a fresh attempt.`;
    }
    case "ERROR": {
      const sentPhrase = relPhrase(sent_at, (r) => ` (link sent ${r})`);
      return `Processing the pro's interview failed${sentPhrase}. Send a new link to try again.`;
    }
    case "NONE":
      return "This pro hasn't been sent an onboarding interview.";
    default: {
      // A state this build doesn't recognise (the API widens the enum ahead of the
      // generated client). Stay non-committal — never claim no invite was sent, which
      // would be worse than saying nothing.
      const sentPhrase = relPhrase(sent_at, (r) => ` — the last invite was sent ${r}`);
      return `Interview status unavailable${sentPhrase}. This page doesn't recognise the pro's current status yet; try reloading.`;
    }
  }
}

export function AiInterviewCard({
  state,
  sent_at,
  expires_at,
  completed_at,
  isLoading = false,
  canSend = true,
  onSend,
}: Readonly<AiInterviewCardProps>) {
  const body = getBodyText({ state, sent_at, expires_at, completed_at });

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-light-gray p-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-normal font-semibold">AI Onboarding Interview</h3>
        <AiInterviewStateChip state={state} />
      </div>
      <p className="text-small text-gray">{isLoading ? "Loading interview status…" : body}</p>
      <div>
        <Button size="small" variant="contained" onClick={onSend} disabled={isLoading || !canSend}>
          Send interview link
        </Button>
      </div>
    </section>
  );
}
